import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-input',
  templateUrl: './app-input.component.html',
  styleUrls: ['./app-input.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppInputComponent),
      multi: true
    }
    // If you plan to add validation inside app-input, you'd also add NG_VALIDATORS here:
    // {
    //   provide: NG_VALIDATORS,
    //   useExisting: forwardRef(() => appInputComponent),
    //   multi: true
    // }
  ]
})
export class AppInputComponent implements OnInit, OnChanges, ControlValueAccessor { // Implement ControlValueAccessor

  // --- Core Input Properties (from parent component) ---
  @Input() inputLabel: string = '';
  @Input() placeholder: string = '';
  // Removed @Input() value as ControlValueAccessor manages the internal value
  @Input() controlName: string = '';
  @Input() inputId: string = '';
  @Input() isFocused: boolean = false;
  @Input() additionalClasses: string | string[] = '';

  // --- Input Element Specific Properties ---
  @Input() type: 'text' | 'password' | 'tel' | 'email' | 'numeric' | 'decimal' | 'search' | 'select' | 'area-modal' | 'textarea' = 'text';
  @Input() inputMode: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search' | 'textarea' = 'text';
  @Input() maxLength: number | null = null;
  @Input() readOnly: boolean = false;
  @Input() autocomplete: string = 'off';

  // --- New Properties for Ion-Select & Area Modal ---
  @Input() selectOptions?: { id: any; name: string; delivery_start_time?: string; delivery_end_time?: string; }[] = [];
  @Input() displayValue: string = '';
  @Input() showDropdownArrow: boolean = false;

  // --- Icon Properties ---
  @Input() leadingIconSrc: string = '';
  @Input() leadingIconName: string = '';

  // --- Password Toggle Properties ---
  @Input() showPasswordToggle: boolean = false;
  @Input() passwordVisible: boolean = false;

  // --- Output Events (to parent component) - Keep these for other custom events, but valueChange is handled by CVA ---
  @Output() rawInput = new EventEmitter<Event>();
  @Output() focusEvent = new EventEmitter<string>();
  @Output() blurEvent = new EventEmitter<string>();
  @Output() leadingIconClick = new EventEmitter<string>();
  @Output() togglePasswordVisibility = new EventEmitter<boolean>();
  @Output() selectValueChange = new EventEmitter<any>();
  @Output() modalOpenRequest = new EventEmitter<void>();

  // Internal value managed by ControlValueAccessor
  private _value: any = ''; // Internal variable to hold the value

  // Functions to be called when the control's value changes or it loses focus
  onChange: any = () => {};
  onTouched: any = () => {};

  // Internal type for password input
  internalInputType: string = 'text';

  constructor() { }

  ngOnInit() {
    this.updateInternalInputType();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['passwordVisible'] || changes['type']) {
      this.updateInternalInputType();
    }
    // If the input 'value' was still being used, you might need to handle changes['value'] here,
    // but with CVA, writeValue handles initial setting.
  }

  private updateInternalInputType(): void {
    if (this.showPasswordToggle && this.type === 'password') {
      this.internalInputType = this.passwordVisible ? 'text' : 'password';
    } else {
      this.internalInputType = this.type;
    }
  }

  // --- ControlValueAccessor Methods ---
  writeValue(value: any): void {
    // This method is called by Angular to write a value into the DOM.
    // It's how the parent's [(ngModel)] gets its initial value into your component.
    this._value = value;
  }

  registerOnChange(fn: any): void {
    // This method is called by Angular to register a function
    // that Angular will call when the control's value changes in the UI.
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    // This method is called by Angular to register a function
    // that Angular will call when the control loses focus.
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Optional method to disable/enable the control from the outside
    this.readOnly = isDisabled; // Assuming `readOnly` can act as disabled state
  }

  // --- Event Handlers - Now call onChange() to notify ngModel ---

  onInputChange(event: Event): void {
    if (this.type !== 'select') {
      const inputElement = event.target as HTMLInputElement;
      this._value = inputElement.value; // Update internal value
      this.onChange(this._value); // Notify ngModel of the change
      this.rawInput.emit(event); // Emit your custom raw event if still needed
    }
  }

  onSelectChange(event: any): void {
    this._value = event.detail?.value; // Update internal value
    this.onChange(this._value); // Notify ngModel of the change
    this.selectValueChange.emit(this._value); // Emit your custom select change event if still needed
  }

  onFocus(): void {
    this.focusEvent.emit(this.controlName);
  }

  onBlur(): void {
    this.onTouched(); // Notify ngModel that the control has been touched
    this.blurEvent.emit(this.controlName);
  }

  onLeadingIconClick(): void {
    this.leadingIconClick.emit(this.controlName);
  }

  onTogglePasswordVisibility(): void {
    this.togglePasswordVisibility.emit(!this.passwordVisible);
  }

  onAreaModalClick(): void {
    this.modalOpenRequest.emit();
  }

  // Getter for the value to be used in the template's [value] binding
  get currentValue(): any {
    return this._value;
  }

  get focusClass(): string {
    return this.isFocused ? 'focused-input' : '';
  }

  get fieldsetClasses(): string[] {
    const classes: string[] = ['fieldset'];
    if (typeof this.additionalClasses === 'string') {
      classes.push(this.additionalClasses);
    } else if (Array.isArray(this.additionalClasses)) {
      classes.push(...this.additionalClasses);
    }
    return classes;
  }
}